import React, { useState } from "react";
import { LinkedinLogo, Spinner, UploadSimple, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function ImportSalesNavigator({ open, onOpenChange, onImported }) {
  const [csvText, setCsvText] = useState("");
  const [defaultState, setDefaultState] = useState("");
  const [defaultIndustry, setDefaultIndustry] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [importing, setImporting] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setCsvText(text);
    setPreview(null);
  };

  const runPreview = async () => {
    if (!csvText.trim()) {
      toast.error("Paste CSV text or upload a file");
      return;
    }
    setPreviewing(true);
    setPreview(null);
    try {
      const { data } = await api.post("/leads/import-sales-navigator/preview", {
        csv_text: csvText,
        default_state: defaultState,
        default_industry: defaultIndustry,
      });
      setPreview(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setPreviewing(false);
    }
  };

  const runImport = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/leads/import-sales-navigator", {
        csv_text: csvText,
        default_state: defaultState,
        default_industry: defaultIndustry,
      });
      toast.success(
        `Imported ${data.imported} leads${data.skipped_duplicates ? ` · ${data.skipped_duplicates} duplicates skipped` : ""}`,
      );
      onImported?.(data.leads || []);
      onOpenChange(false);
      // Reset
      setCsvText("");
      setPreview(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="salesnav-modal"
        className="bg-zinc-950 border-zinc-800 max-w-3xl rounded-sm text-zinc-100"
      >
        <DialogHeader>
          <div className="flex items-center gap-2">
            <LinkedinLogo size={22} weight="duotone" className="text-orange-500" />
            <DialogTitle className="font-display text-zinc-50">Import LinkedIn Sales Navigator</DialogTitle>
          </div>
          <DialogDescription className="text-zinc-500 text-sm">
            Export a lead list from Sales Navigator as CSV (Lists → ⋯ → Export) then paste the contents
            below or upload the file. Common columns are auto-mapped.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
          <div className="md:col-span-3">
            <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">CSV file or paste below</Label>
            <div className="mt-1.5 flex items-center gap-2">
              <label className="inline-flex">
                <input
                  type="file"
                  accept=".csv"
                  data-testid="salesnav-file-input"
                  onChange={handleFile}
                  className="hidden"
                />
                <span className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-zinc-800 hover:bg-zinc-900 text-zinc-200 rounded-sm cursor-pointer">
                  <UploadSimple size={14} /> Choose .csv
                </span>
              </label>
              <span className="text-xs text-zinc-500 font-mono">or paste raw CSV below</span>
            </div>
            <Textarea
              data-testid="salesnav-csv-text"
              value={csvText}
              onChange={(e) => { setCsvText(e.target.value); setPreview(null); }}
              rows={6}
              placeholder='First Name,Last Name,Title,Company,Industry,Location,LinkedIn URL&#10;John,Smith,Owner,Smith Roofing,Construction,"Sioux Falls, SD",https://linkedin.com/in/johnsmith'
              className="mt-2 bg-zinc-900 border-zinc-800 font-mono text-xs"
            />
          </div>

          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Default state (optional)</Label>
            <Input
              data-testid="salesnav-default-state"
              value={defaultState}
              onChange={(e) => setDefaultState(e.target.value)}
              placeholder="e.g. South Dakota"
              className="mt-1.5 bg-zinc-900 border-zinc-800"
            />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Default industry (optional)</Label>
            <Input
              data-testid="salesnav-default-industry"
              value={defaultIndustry}
              onChange={(e) => setDefaultIndustry(e.target.value)}
              placeholder="e.g. Roofing"
              className="mt-1.5 bg-zinc-900 border-zinc-800"
            />
          </div>
          <div className="flex items-end">
            <Button
              data-testid="salesnav-preview-btn"
              onClick={runPreview}
              disabled={previewing}
              variant="outline"
              className="w-full border-zinc-800 hover:bg-zinc-900 rounded-sm"
            >
              {previewing ? (<><Spinner size={14} className="mr-1.5 animate-spin" /> Parsing…</>) : "Preview"}
            </Button>
          </div>
        </div>

        {preview && (
          <div className="mt-2 border border-zinc-800 rounded-sm overflow-hidden" data-testid="salesnav-preview">
            <div className="px-3 py-2 bg-zinc-900 border-b border-zinc-800 flex items-center justify-between">
              <div className="text-xs text-zinc-300 font-mono">
                {preview.total} parsed · <span className="text-amber-400">{preview.duplicates} duplicates</span> · <span className="text-emerald-400">{preview.total - preview.duplicates} will import</span>
              </div>
            </div>
            <div className="max-h-[260px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-zinc-950 sticky top-0">
                  <tr>
                    {["Business", "Owner", "City", "Industry", ""].map((h) => (
                      <th key={h} className="px-2 py-1.5 text-left font-mono text-[10px] uppercase text-zinc-500 border-b border-zinc-800">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r, i) => (
                    <tr key={i} className="border-b border-zinc-800/40">
                      <td className="px-2 py-1.5 text-zinc-200">{r.business_name}</td>
                      <td className="px-2 py-1.5 text-zinc-400">{r.owner_name || "—"}</td>
                      <td className="px-2 py-1.5 text-zinc-400">{r.city || "—"}{r.state ? `, ${r.state}` : ""}</td>
                      <td className="px-2 py-1.5 text-zinc-400">{r.industry || "—"}</td>
                      <td className="px-2 py-1.5">
                        {r.is_duplicate
                          ? <span className="inline-flex items-center gap-1 text-amber-400 text-[10px]"><WarningCircle size={10} weight="fill" /> dup</span>
                          : <span className="inline-flex items-center gap-1 text-emerald-400 text-[10px]"><CheckCircle size={10} weight="fill" /> new</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <DialogFooter className="pt-3 border-t border-zinc-800">
          <Button
            variant="outline"
            data-testid="salesnav-cancel"
            onClick={() => onOpenChange(false)}
            className="border-zinc-800 hover:bg-zinc-900 rounded-sm"
          >
            Cancel
          </Button>
          <Button
            data-testid="salesnav-import-btn"
            onClick={runImport}
            disabled={importing || !csvText.trim()}
            className="bg-orange-600 hover:bg-orange-500 text-zinc-950 rounded-sm"
          >
            {importing ? (<><Spinner size={14} className="mr-1.5 animate-spin" /> Importing…</>) : (<><UploadSimple size={14} className="mr-1.5" /> Import {preview ? `${preview.total - preview.duplicates} leads` : ""}</>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
